import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ProfileHeader from './components/ProfileHeader';
import StatsOverview from './components/StatsOverview';
import AchievementBadges from './components/AchievementBadges';
import ContributionHistory from './components/ContributionHistory';
import ContributionPatternAnalysis from './components/ContributionPattern';
import EditHistoryModal from './components/EditHistoryModal';
import {
  MemberInfo,
  ContributionStats,
  ContributionRecord,
  Achievement,
  ContributionPattern,
  EditHistoryEntry } from
'./types';

const MemberProfile = () => {
  const navigate = useNavigate();
  const { memberId } = useParams<{ memberId?: string }>();
  const { user, userProfile } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [showEditHistory, setShowEditHistory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [memberInfo, setMemberInfo] = useState<MemberInfo | null>(null);
  const [contributionStats, setContributionStats] = useState<ContributionStats>({
    totalContributions: 0,
    totalAmount: 0,
    averageAmount: 0,
    participationRate: 0,
    currentStreak: 0,
    longestStreak: 0
  });
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [contributionRecords, setContributionRecords] = useState<ContributionRecord[]>([]);
  const [contributionPattern, setContributionPattern] = useState<ContributionPattern>({
    frequency: 'weekly',
    averageAmount: 0,
    consistency: 0,
    preferredDays: []
  });
  const [editHistory, setEditHistory] = useState<EditHistoryEntry[]>([]);
  
  const targetMemberId = memberId || user?.id;
  const isOwnProfile = !memberId || memberId === user?.id;
  const currentUserRole = userProfile?.role === 'admin' || userProfile?.role === 'super_admin' ? 'admin' : 'member';

  // Redirect super admin away from profile page immediately
  useEffect(() => {
    if (userProfile?.role === 'super_admin') {
      navigate('/super-admin-dashboard');
      return;
    }
  }, [userProfile, navigate]);

  // Fetch member profile and data from Supabase
  useEffect(() => {
    // Don't fetch if super admin
    if (userProfile?.role === 'super_admin') return;
    
    const fetchMemberData = async () => {
      if (!targetMemberId || !user) return;

      try {
        setLoading(true);
        setError(null);

        // Get user's group first
        const { data: memberGroup, error: groupError } = await supabase
          .from('group_members')
          .select('group_id')
          .eq('user_id', targetMemberId)
          .limit(1)
          .single();

        if (groupError || !memberGroup) {
          setError('Member not found in any group');
          setLoading(false);
          return;
        }

        const groupId = memberGroup.group_id;

        // Fetch member profile
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', targetMemberId)
          .single();

        if (profileError) throw profileError;

        // Get member's role in group
        const { data: groupMember, error: memberError } = await supabase
          .from('group_members')
          .select('role, joined_at')
          .eq('user_id', targetMemberId)
          .eq('group_id', groupId)
          .single();

        const memberRole = groupMember?.role || 'member';
        const joinDate = groupMember?.joined_at ? new Date(groupMember.joined_at) : new Date(profile.created_at);

        setMemberInfo({
          id: profile.id,
          name: profile.full_name || 'Unknown User',
          email: profile.email || '',
          role: memberRole,
          joinDate: joinDate,
          avatar: profile.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(profile.full_name || 'User')}`,
          phone: profile.phone || '',
          address: ''
        });

        // Fetch all contributions for this member
        const { data: contributions, error: contribError } = await supabase
          .from('contributions')
          .select(`
            id,
            amount,
            contribution_date,
            notes,
            status,
            recorded_by,
            created_at,
            recorder:user_profiles!recorded_by (
              id,
              full_name
            )
          `)
          .eq('group_id', groupId)
          .eq('member_id', targetMemberId)
          .order('contribution_date', { ascending: false });

        if (contribError) throw contribError;

        // Map contributions to records
        const records: ContributionRecord[] = (contributions || []).map((contrib: any) => ({
          id: contrib.id,
          date: new Date(contrib.contribution_date),
          amount: parseFloat(contrib.amount || 0),
          recordedBy: contrib.recorder?.full_name || 'Unknown',
          recordedById: contrib.recorded_by || '',
          recordingDate: contrib.created_at ? new Date(contrib.created_at) : undefined,
          verificationStatus: contrib.status === 'approved' ? 'verified' : 
                             contrib.status === 'pending_approval' ? 'pending' : 
                             contrib.status === 'rejected' ? 'rejected' : 'verified',
          notes: contrib.notes || undefined
        }));

        setContributionRecords(records);

        // Calculate stats
        const totalContributions = records.length;
        const totalAmount = records.reduce((sum, r) => sum + r.amount, 0);
        const averageAmount = totalContributions > 0 ? totalAmount / totalContributions : 0;
        
        // Get total group members for participation rate
        const { count: totalMembers } = await supabase
          .from('group_members')
          .select('*', { count: 'exact', head: true })
          .eq('group_id', groupId);
        
        const participationRate = totalMembers && totalMembers > 0 
          ? (totalContributions / (totalMembers * 4)) * 100 // Assuming weekly contributions
          : 0;

        // Calculate streaks
        const sortedDates = records
          .filter(r => r.verificationStatus === 'verified')
          .map(r => new Date(r.date).toDateString())
          .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());

        let currentStreak = 0;
        let longestStreak = 0;
        let tempStreak = 0;
        const today = new Date().toDateString();

        for (let i = 0; i < sortedDates.length; i++) {
          if (i === 0 && sortedDates[i] === today) {
            currentStreak = 1;
            tempStreak = 1;
          } else if (i > 0) {
            const prevDate = new Date(sortedDates[i - 1]);
            const currDate = new Date(sortedDates[i]);
            const diffDays = Math.floor((prevDate.getTime() - currDate.getTime()) / (1000 * 60 * 60 * 24));
            
            if (diffDays === 7) { // Weekly contributions
              tempStreak++;
              if (i === 1 && sortedDates[0] === today) {
                currentStreak = tempStreak;
              }
            } else {
              longestStreak = Math.max(longestStreak, tempStreak);
              tempStreak = 1;
            }
          }
        }
        longestStreak = Math.max(longestStreak, tempStreak);

        setContributionStats({
          totalContributions,
          totalAmount,
          averageAmount,
          participationRate: Math.min(participationRate, 100),
          currentStreak,
          longestStreak
        });

        // Calculate contribution pattern
        const dayCounts: Record<string, number> = {};
        records.forEach(r => {
          const day = new Date(r.date).toLocaleDateString('en-US', { weekday: 'long' });
          dayCounts[day] = (dayCounts[day] || 0) + 1;
        });
        const preferredDays = Object.entries(dayCounts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 2)
          .map(([day]) => day);

        setContributionPattern({
          frequency: 'weekly',
          averageAmount,
          consistency: participationRate,
          preferredDays
        });

        // Generate achievements based on real data
        const newAchievements: Achievement[] = [];
        if (currentStreak >= 30) {
          newAchievements.push({
            id: 'consistency-30',
            title: 'Consistent Contributor',
            description: 'Made contributions for 30 consecutive weeks',
            icon: 'Flame',
            earnedDate: new Date(),
            category: 'consistency'
          });
        }
        if (totalAmount >= 5000) {
          newAchievements.push({
            id: 'milestone-5000',
            title: 'Milestone Reached',
            description: `Contributed a total of ${totalAmount.toLocaleString()} VT`,
            icon: 'Trophy',
            earnedDate: new Date(),
            category: 'milestone'
          });
        }
        if (participationRate >= 90) {
          newAchievements.push({
            id: 'participation-90',
            title: 'Perfect Attendance',
            description: `${participationRate.toFixed(1)}% participation rate`,
            icon: 'Award',
            earnedDate: new Date(),
            category: 'participation'
          });
        }
        setAchievements(newAchievements);

        // Edit history would come from audit_logs if available
        setEditHistory([]);

      } catch (err: any) {
        console.error('Error fetching member data:', err);
        setError(err.message || 'Failed to load member profile');
      } finally {
        setLoading(false);
      }
    };

    fetchMemberData();
  }, [targetMemberId, user, userProfile]);


  const handleEditProfile = () => {
    // Navigate to account settings page where users can edit their profile
    navigate('/account-settings');
  };

  const handleSendMessage = () => {
    console.log('Send message clicked');
  };

  const handleEditContribution = (record: ContributionRecord) => {
    console.log('Edit contribution:', record);
  };

  const handleVerifyContribution = (record: ContributionRecord) => {
    console.log('Verify contribution:', record);
  };

  const handleExportData = () => {
    console.log('Export data clicked');
  };

  // Don't render if super admin (will redirect)
  if (userProfile?.role === 'super_admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <Header
        onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        isMobileMenuOpen={isMobileMenuOpen} />


      <div className="flex">
        <div className="hidden lg:block">
          <Sidebar
            isCollapsed={isSidebarCollapsed}
            userRole={currentUserRole} />

        </div>

        <main
          className={`
            flex-1 transition-layout
            ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}
          `}>

          <div className="p-6 max-w-7xl mx-auto">
            <div className="mb-6">
              <BreadcrumbTrail />
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  Member Profile
                </h1>
                <p className="text-muted-foreground">
                  View and manage member contribution history and account details
                </p>
              </div>

              <div className="flex flex-wrap gap-3">
                <Button
                  variant="outline"
                  iconName="History"
                  iconPosition="left"
                  onClick={() => setShowEditHistory(true)}>

                  Edit History
                </Button>
                <Button
                  variant="outline"
                  iconName="Download"
                  iconPosition="left"
                  onClick={handleExportData}>

                  Export Data
                </Button>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading member profile...</p>
                </div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
                  <p className="text-destructive mb-4">{error}</p>
                  <Button onClick={() => navigate('/member-dashboard')}>
                    Go to Dashboard
                  </Button>
                </div>
              </div>
            ) : memberInfo ? (
              <div className="space-y-6">
                <ProfileHeader
                  member={memberInfo}
                  isOwnProfile={isOwnProfile}
                  onEdit={handleEditProfile}
                  onMessage={handleSendMessage} />

                <StatsOverview stats={contributionStats} />

                <AchievementBadges achievements={achievements} />

                <ContributionPatternAnalysis pattern={contributionPattern} />

                <ContributionHistory
                  records={contributionRecords}
                  canEdit={currentUserRole === 'admin'}
                  onEdit={handleEditContribution}
                  onVerify={handleVerifyContribution} />
              </div>
            ) : null}

            <div className="mt-8 p-6 bg-card rounded-md border border-border shadow-elevation-1">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="Shield" size={24} color="var(--color-primary)" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    Data Security & Privacy
                  </h3>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Icon name="Lock" size={16} className="text-success" />
                      <span>All contribution data is encrypted using industry-standard AES-256 encryption</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="FileText" size={16} className="text-success" />
                      <span>Complete audit trail maintained for all changes and edits</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="Eye" size={16} className="text-success" />
                      <span>Access logs recorded for compliance and security monitoring</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      <EditHistoryModal
        isOpen={showEditHistory}
        onClose={() => setShowEditHistory(false)}
        history={editHistory} />

    </div>);

};

export default MemberProfile;